// src/views/Login/CreatePassword.tsx

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTokenValidation } from '@/domains/auth/hooks/useTokenValidation';
import { NAVBAR_SOURCES } from '@/components/AppComponents/Sidebar/NavBar/NavBarSources';
import AuthLayout from '@/components/AppComponents/Layout/AuthLayout';
import i18n from '@/shared/i18n/i18n';
import { Form } from '@/components/AppComponents/Form/Form';
import {
  CreatePasswordFormValues,
  createPasswordSchema,
} from './schemas/createPassword.schema';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { TextInput } from '@/components/UI/components/Textfield';
import { Button } from '@/components/UI/components/Button';
import { useAuthStore } from '@/domains/auth/auth.store';

import {
  otpForgotPasswordUseCase,
  useChangePasswordUseCase,
} from '@/domains/auth/auth.usecases';
import { OtpFormValues, otpSchema } from './schemas/login.schema';
import { Callout } from '@/components/UI/components/Callout';
import { RiCheckboxCircleFill } from '@remixicon/react';
import { usePasswordStrength } from '@/domains/auth/hooks/usePasswordStrength';
import { TrafficLights } from '@/components/UI/components/TrafficLights';
import { Icon } from '@/components/UI/components/Icon';

const CreatePassword = () => {
  const [formValues, setFormValues] = useState<CreatePasswordFormValues | null>(
    null,
  );
  const [isLoadingPwd, setIsLoadingPwd] = useState(false);
  const [isSentPwd, setIsSentPwd] = useState(false);
  const [isLoadingOtp, setIsLoadingOtp] = useState(false);
  const [otpIsSent, setOtpIsSent] = useState(false);

  const { token, isValid, error } = useTokenValidation();
  const navigate = useNavigate();
  const otpForgotPassword = otpForgotPasswordUseCase();
  const changePassword = useChangePasswordUseCase();
  const { setTokens, clearTokens } = useAuthStore();

  useEffect(() => {
    if (token && isValid) {
      setTokens(token, '');
    }
  }, [token, isValid, setTokens]);

  const createPasswordMethods = useForm<CreatePasswordFormValues>({
    resolver: zodResolver(createPasswordSchema),
  });

  const otpMethods = useForm<OtpFormValues>({
    resolver: zodResolver(otpSchema),
  });

  const password = createPasswordMethods.watch('password');
  const { color, label, strength, acceptance } = usePasswordStrength(password);

  const handleCreatePassword = async (data: CreatePasswordFormValues) => {
    setFormValues(data);
    try {
      setIsLoadingPwd(true);
      await otpForgotPassword();
      setIsSentPwd(true);
    } catch (er) {
      console.error(er);
    } finally {
      setIsLoadingPwd(false);
    }
  };

  const handleOtpSubmit = async (data: OtpFormValues) => {
    if (!formValues) return;
    try {
      setIsLoadingOtp(true);
      await changePassword({ password: formValues.password, otp: data.otp });
      setOtpIsSent(true);
      clearTokens();
    } catch (er) {
      console.error(er);
    } finally {
      setIsLoadingOtp(false);
    }
  };

  return (
    <AuthLayout title={i18n.t('createPassword.title')}>
      {otpIsSent ? (
        <>
          <div className="flex flex-col justify-start items-stretch space-y-8">
            <Callout
              title={i18n.t('createPassword.successMessage')}
              icon={RiCheckboxCircleFill}
              color="green"
            />
            <Button
              variant="primary"
              onClick={() => navigate(NAVBAR_SOURCES.login)}
              className="w-full"
            >
              {i18n.t('forgotPassword.back')}
            </Button>
          </div>
        </>
      ) : (
        <>
          {error ? (
            <div className="mb-6">
              <Callout title="" color="red">
                {i18n.t('createPassword.errors.tokenExpired')}
              </Callout>
            </div>
          ) : (
            <div className="mb-6 text-gray-200">
              <p>{i18n.t('createPassword.leadIn')}</p>
              <p className="mt-4">{i18n.t('createPassword.sentence')}</p>
              <ul>
                <li className="flex items-center">
                  <Icon
                    icon={RiCheckboxCircleFill}
                    color={acceptance.uppercase ? 'green' : 'gray'}
                    className="transition-all duration-300"
                  />
                  <span
                    className={
                      acceptance.uppercase ? 'text-gray-200' : 'text-gray-300'
                    }
                  >
                    {i18n.t('generic.passwordRules.uppercase')}
                  </span>
                </li>
                <li className="flex items-center">
                  <Icon
                    icon={RiCheckboxCircleFill}
                    color={acceptance.lowercase ? 'green' : 'gray'}
                    className="transition-all duration-300"
                  />
                  <span
                    className={
                      acceptance.lowercase ? 'text-gray-200' : 'text-gray-300'
                    }
                  >
                    {i18n.t('generic.passwordRules.lowercase')}
                  </span>
                </li>
                <li className="flex items-center">
                  <Icon
                    icon={RiCheckboxCircleFill}
                    color={acceptance.number ? 'green' : 'gray'}
                    className="transition-all duration-300"
                  />
                  <span
                    className={
                      acceptance.number ? 'text-gray-200' : 'text-gray-300'
                    }
                  >
                    {i18n.t('generic.passwordRules.number')}
                  </span>
                </li>
                <li className="flex items-center">
                  <Icon
                    icon={RiCheckboxCircleFill}
                    color={acceptance.special ? 'green' : 'gray'}
                    className="transition-all duration-300"
                  />
                  <span
                    className={
                      acceptance.special ? 'text-gray-200' : 'text-gray-300'
                    }
                  >
                    {i18n.t('generic.passwordRules.special')}
                  </span>
                </li>
                <li className="flex items-center">
                  <Icon
                    icon={RiCheckboxCircleFill}
                    color={acceptance.length ? 'green' : 'gray'}
                    className="transition-all duration-300"
                  />
                  <span
                    className={
                      acceptance.length ? 'text-gray-200' : 'text-gray-300'
                    }
                  >
                    {i18n.t('generic.passwordRules.length')}
                  </span>
                </li>
              </ul>
            </div>
          )}
          <Form<CreatePasswordFormValues>
            onSubmit={handleCreatePassword}
            methods={createPasswordMethods}
          >
            <div className="flex flex-col justify-between space-y-8">
              <div className="flex flex-col justify-start items-baseline space-y-2 w-full">
                <label htmlFor="password" className="label text-gray-200">
                  {i18n.t('createPassword.password')}
                </label>
                <TextInput
                  type="password"
                  autoFocus
                  placeholder=""
                  disabled={!!error || isSentPwd}
                  {...createPasswordMethods.register('password')}
                  error={
                    !!createPasswordMethods.formState.errors.password?.message
                  }
                  errorMessage={
                    createPasswordMethods.formState.errors.password?.message
                  }
                />
                {!error && (
                  <TrafficLights
                    color={color}
                    label={label}
                    maxStrength={5}
                    strength={strength}
                  />
                )}
              </div>
              <div className="flex flex-col justify-start items-baseline space-y-2 w-full">
                <label
                  htmlFor="confirmPassword"
                  className="label text-gray-200"
                >
                  {i18n.t('createPassword.passwordConfirm')}
                </label>
                <TextInput
                  type="password"
                  placeholder=""
                  disabled={!!error || isSentPwd}
                  {...createPasswordMethods.register('confirmPassword')}
                  error={
                    !!createPasswordMethods.formState.errors.confirmPassword
                      ?.message
                  }
                  errorMessage={
                    createPasswordMethods.formState.errors.confirmPassword
                      ?.message
                  }
                />
              </div>
              <Button
                type="submit"
                loading={isLoadingPwd}
                disabled={!!error || isSentPwd}
                variant="primary"
                className="w-full"
              >
                {i18n.t('createPassword.submit')}
              </Button>
            </div>
          </Form>

          {/* OTP Form */}
          {isSentPwd && (
            <Form<OtpFormValues>
              onSubmit={handleOtpSubmit}
              methods={otpMethods}
            >
              <div className="flex flex-col justify-between mt-6 pt-4 border-t space-y-8 border-revving-border dark:border-darkRevving-border">
                <div className="space-y-4 text-gray-200">
                  <p>{i18n.t('createPassword.otpSentence')}</p>
                  <div className="flex flex-col justify-start items-baseline space-y-2 w-full">
                    <label
                      htmlFor="otp"
                      className="text-sm font-accent text-gray-200 pl-1"
                    >
                      {i18n.t('login.otp')}
                    </label>
                    <TextInput
                      type="text"
                      autoFocus
                      {...otpMethods.register('otp')}
                      error={!!otpMethods.formState.errors.otp}
                      errorMessage={otpMethods.formState.errors.otp?.message}
                    />
                  </div>
                </div>
                <Button
                  type="submit"
                  id="otp-submit"
                  loading={isLoadingOtp}
                  disabled={isLoadingOtp}
                  variant="primary"
                  className="w-full"
                >
                  {i18n.t('createPassword.otpSubmit')}
                </Button>
              </div>
            </Form>
          )}
        </>
      )}
    </AuthLayout>
  );
};

export default CreatePassword;
